/***
 *
 * Store Locator with Google Maps Integration
 *
 * Creates a list of stores which plot markers on a Google Maps embed
 *
 * @param {object} (required) config - An object containing all our initialization data
 * @param {boolean} (required) config.zoom - The zoom level the map will initialize at
 * @param {string} (optional) config.markerImage - A URL to a file override for the map marker pin
 * @param {boolean} (optional) config.grayScale - When true, renders the map in grayscale
 * @param {float} (required) config.defaultLatitude - The default latitude the map will initialize at if we are unable to determine user location
 * @param {float} (required) config.defaultLongitude - The default longitude the map will initialize at if we are unable to determine user location
 * @param {string} (required) config.dataType - How the component will receive location data. server (external API) / blocks (Shopify section blocks)
 * @param {string} (optional) config.apiUrl - The external API URL which will return a GeoJson payload if config.dataType is set to `server`
 * @param {Object} (optional) config.locations - A GeoJson object  (https://geojson.org/) generated by Shopify Section Blocks used in place of an API
 *
 *
 */
if (!customElements.get('store-locator')) {
    class StoreLocator extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.initialize();

            // Google recommends deferring asking for user location until the component is in-view to build user trust
            this.userLocationHandler = this.getUserLocation.bind(this);
            this.visibleObserver = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        this.userLocationHandler();
                        this.visibleObserver.disconnect();
                    }
                },
                { threshold: [0] }
            );
            this.visibleObserver.observe(this);
            this.watch();
        }

        disconnectedCallback() {
            this.observer?.disconnect();
            this.visibleObserver?.disconnect();
        }

        // This is mostly for Storybook mutations
        // It adds support for on the fly updating based on the internal JSON config changing (which is outside the scope of a normal custom-element self watching)
        watch() {
            const targetNode = this.querySelector("script[id='application/json']");
            const config = {
                attributes: true,
                childList: true,
                subtree: true,
                characterData: true
            };
            if (!targetNode) return;

            const callback = (mutationList, observer) => {
                for (const mutation of mutationList) {
                    if (mutation.type === 'characterData') {
                        const newConfig = targetNode.textContent;
                        let newJSON = null;
                        try {
                            newJSON = JSON.parse(newConfig);
                        } catch (e) {
                            console.error(e);
                        }
                        const originalComparison = JSON.stringify(this.config);
                        const newComparison = JSON.stringify(newJSON);
                        if (originalComparison !== newComparison) {
                            this.initialize();
                            this.initializeMap();
                        }
                    }
                }
            };
            this.observer = new MutationObserver(callback);
            this.observer.observe(targetNode, config);
        }

        async initialize() {
            this.config = this.querySelector("script[type='application/json']");
            if (!this.config) {
                console.error('Config not found.');
                return;
            }
            this.config = this.config.textContent;
            this.config = JSON.parse(this.config);

            this.abortController = new AbortController();

            this.zoom = window.innerWidth <= 750 ? this.config['zoom-mobile'] : this.config.zoom;
            this.markerImage = this.config.marker;
            this.grayScale = this.config.grayScale;
            this.defaultLatitude = this.config.latitude;
            this.defaultLongitude = this.config.longitude;
            this.dataType = this.config.dataType;
            this.apiUrl = this.config.apiUrl;
            this.blockLocations = this.config.locations;
            this.dataTypes = {
                server: 'server',
                blocks: 'blocks'
            };
            if (this.dataType === this.dataTypes.server && !this.apiUrl) {
                console.error('API URL must be set to use the server data type.');
                this.dataType = this.dataTypes.blocks;
            }
            this.locations = this.dataType === this.dataTypes.server ? [] : this.blockLocations;

            this.mapContainer = this.querySelector('[data-map-container]');
            this.loader = this.querySelector('.store-locator__loader');

            this.map = null;
            this.infoWindow = null;
            this.originLocation = {
                lat: this.defaultLatitude,
                lng: this.defaultLongitude
            };
            this.distanceSortedStores = [];
            this.userLocation = {};

            if (
                !this.mapContainer ||
                !this.defaultLatitude ||
                !this.defaultLongitude ||
                !this.zoom
            ) {
                this.classList.add('hidden');
                console.error('Required components not found.');
                return;
            }

            await this.initializeMap();
            this.fetchLocations();
        }

        getUserLocation() {
            if (!navigator.geolocation) {
                console.info('Geolocation not supported. Map will use the default position.');
                if (!this.locations || !Object.keys(this.locations).length) this.fetchLocations();
                return;
            }

            const success = (location) => {
                this.userLocation.lat = location.coords.latitude;
                this.userLocation.lng = location.coords.longitude;
                this.updateMapCenter(this.userLocation);
                this.fetchLocations();
            };

            const failure = () => {
                console.info('Geolocation not supported. Map will use default position.');
                if (!this.locations || !Object.keys(this.locations).length) this.fetchLocations();
                return;
            };

            navigator.geolocation.getCurrentPosition(success, failure);
        }

        async initializeMap() {
            const component = this;
            const { Map } = await google.maps.importLibrary('maps');
            const styles = [{ stylers: [] }];
            if (component.grayScale) styles[0].stylers.push({ saturation: -100 });
            component.map = new Map(component.mapContainer, {
                center: {
                    lat: component.userLocation.lat
                        ? component.userLocation.lat
                        : component.originLocation.lat,
                    lng: component.userLocation.lng
                        ? component.userLocation.lng
                        : component.originLocation.lng
                },
                zoom: component.zoom,
                mapTypeId: 'roadmap',
                styles: styles
            });

            if (component.markerImage) {
                component.map.data.setStyle((feature) => {
                    return {
                        icon: {
                            url: component.markerImage,
                            scaledSize: new google.maps.Size(48, 48)
                        }
                    };
                });
            }

            component.infoWindow = new google.maps.InfoWindow();

            google.maps.event.addListener(component.map, 'idle', function () {
                const newLat = component.map.getCenter().lat();
                const newLng = component.map.getCenter().lng();
                if (
                    newLat == component.originLocation.lat &&
                    newLng == component.originLocation.lng
                ) {
                    return;
                }
                const location = {
                    lat: newLat,
                    lng: newLng
                };
                component.updateMapCenter(location);
                component.fetchLocations();
            });
        }

        updateMapCenter(coordinates) {
            if (!this.map) return;
            this.originLocation = {
                lat: coordinates?.lat || this.originLocation.lat,
                lng: coordinates?.lng || this.originLocation.lng
            };
            this.map.setCenter(this.originLocation);
        }

        updateGeoJson() {
            if (!this.map || !this.map.data) return;

            this.map.data.forEach((feature) => {
                this.map.data.remove(feature);
            });
            this.map.data.addGeoJson(this.locations, {
                idPropertyName: 'storeId'
            });
        }

        sortStores() {
            this.sortStoresDefault();
        }

        sortStoresDefault() {
            // Initialize stores array if not already defined
            if (!this.stores) {
                this.stores = [];
                // Populate stores array from map data
                if (this.map && this.map.data) {
                    this.map.data.forEach((feature) => {
                        const store = {
                            storeId: feature.getProperty('storeId'),
                            name: feature.getProperty('name'),
                            fullAddress: feature.getProperty('fullAddress'),
                            phone: feature.getProperty('phone'),
                            detailsUrl: feature.getProperty('detailsUrl'),
                            geometry: feature.getGeometry()
                        };
                        this.stores.push(store);
                    });
                }
            }

            // Simply show stores in their default order
            this.distanceSortedStores = [...this.stores];
        }

        async createAutocomplete() {
            // Removed all code related to search input
        }

        generateInfoWindow(data) {
            const name = data.getProperty('name');
            const hours = data.getProperty('hours');
            const phone = data.getProperty('phone');
            const street = data.getProperty('street');
            const city = data.getProperty('city');
            const state = data.getProperty('state');
            const postalCode = data.getProperty('postalCode');
            const gMapsUrl = data.getProperty('gMapsUrl');
            const detailsUrl = data.getProperty('detailsUrl');
            const location = data.getGeometry().get();
            const content = `
                <h3>${name}</h3>
                <p>${street}, ${city}, ${state}, ${postalCode}</p>
                <p>Phone: <a href='tel:${phone}' class="link link--phone">${phone}</a></p>
                <p>
                    <a href="${detailsUrl}" class="link link--directions">${window.accessibilityStrings.moreDetails}</a>&nbsp; | &nbsp;
                    <a href="${gMapsUrl}"
                        target="_blank"
                        class="link link--directions"
                    >
                        ${window.accessibilityStrings.getDirections}
                    </a>
                </p>
            `;
            return content;
        }

        bindMarkers() {
            if (!this.map || !this.map.data) return;
            this.map.data.addListener('click', (event) => {
                const content = this.generateInfoWindow(event.feature);
                this.infoWindow.setContent(content);
                this.infoWindow.setPosition(event.feature.getGeometry().get());
                this.infoWindow.setOptions({
                    pixelOffset: new google.maps.Size(0, -30)
                });
                this.infoWindow.open(this.map);
            });
        }

        fetchLocations() {
            if (!this.originLocation) {
                console.error('No map location data.');
                return;
            }

            if (this.dataType !== this.dataTypes.server) {
                this.updateGeoJson();
                this.sortStores();
                this.bindMarkers();
                this.loader?.classList.remove('visible');
            } else {
                this.abortController?.abort();
                this.abortController = new AbortController();

                fetch(this.apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(this.originLocation),
                    signal: this.abortController.signal
                })
                    .then((response) => {
                        if (!response.ok) {
                            return Promise.reject(new Error(response.status));
                        }
                        return response.json();
                    })
                    .then((response) => {
                        if (!response.body) {
                            return Promise.reject(new Error('No response.'));
                        }
                        this.locations = response.body;
                        this.updateGeoJson();
                        this.sortStores();
                        this.bindMarkers();
                        this.loader?.classList.remove('visible');
                        this.classList.remove('hidden');
                    })
                    .catch((error) => {
                        this.classList.add('hidden');
                        console.error(error);
                    });
            }
        }

        async reInitializeMap(overrideData, state) {
            if (overrideData) {
                // this.zoom = overrideData.zoom;
                this.zoom =
                    window.innerWidth <= 750 ? overrideData['zoom-mobile'] : overrideData.zoom;
                this.defaultLatitude = overrideData.latitude;
                this.defaultLongitude = overrideData.longitude;
                this.originLocation = {
                    lat: this.defaultLatitude,
                    lng: this.defaultLongitude
                };
            }

            if (state === 'all') {
                this.locations = this.blockLocations;
            } else {
                const states = state.includes('&')
                    ? state.split('&').map((s) => s.trim())
                    : [state];
                this.locations = this.blockLocations.features.filter((location) => {
                    return states.includes(location.properties.state);
                });
                this.locations = {
                    type: 'FeatureCollection',
                    features: this.locations
                };
            }

            await this.initializeMap();
            this.updateGeoJson();
            this.sortStores();
            this.bindMarkers();
            this.loader?.classList.remove('visible');
        }
    }

    customElements.define('store-locator', StoreLocator);
}

if (!customElements.get('store-locator-tabs')) {
    class StoreLocatorTabs extends HTMLElement {
        constructor() {
            super();
            this.tabs = this.querySelectorAll('button');
            this.locations = document.querySelectorAll('.store-locator-list-item');
            this.handleClick = this.handleClick.bind(this);
            this.activeTab = null; // Keep track of the currently active tab
        }

        connectedCallback() {
            // Set the first tab as active by default
            if (this.tabs.length > 0) {
                this.activeTab = this.tabs[0];
                this.activeTab.classList.add('button--primary');
                this.activeTab.classList.remove('button--secondary');
            }

            this.tabs.forEach((tab) => {
                tab.addEventListener('click', this.handleClick);
            });

            this.updateStatus();
        }

        disconnectedCallback() {
            this.tabs.forEach((tab) => {
                tab.removeEventListener('click', this.handleClick);
            });
        }

        handleClick(event) {
            const clickedTab = event.currentTarget;
            const state = clickedTab.dataset.state;
            const overrideDataString = clickedTab.dataset.override;
            let overrideData = null;

            try {
                overrideData = overrideDataString ? JSON.parse(overrideDataString) : null;
            } catch (error) {
                console.error('Error parsing override data:', error);
            }

            // Update button classes
            if (this.activeTab && this.activeTab !== clickedTab) {
                this.activeTab.classList.remove('button--primary');
                this.activeTab.classList.add('button--secondary');
            }
            clickedTab.classList.add('button--primary');
            clickedTab.classList.remove('button--secondary');
            this.activeTab = clickedTab;

            // Filter locations
            this.locations.forEach((location) => {
                location.classList.add('hidden');
                const itemValue = location.dataset.item;

                if (state === 'all') {
                    location.classList.remove('hidden');
                } else if (state.includes('&')) {
                    const states = state.split('&').map((s) => s.trim());
                    if (states.includes(itemValue)) {
                        location.classList.remove('hidden');
                    }
                } else if (itemValue === state) {
                    location.classList.remove('hidden');
                }
            });

            // Re-initialize map if override data is present
            const storeLocator = document.querySelector('store-locator');
            if (storeLocator) {
                storeLocator.reInitializeMap(overrideData, state);
            }
        }

        updateStatus() {
            if (!p80.storeHours) return;
            this.locations.forEach((location) => {
                const name = location.querySelector('h3')?.textContent.trim();
                const stateEl = location.querySelector('.state');

                if (!name || !stateEl) {
                    console.warn('Store name or state element not found for a location.');
                    return;
                }

                const status = p80.storeHours[name];
                if (status) {
                    stateEl.textContent = status;
                    stateEl.classList.toggle('close', status === 'closed');
                    stateEl.classList.toggle('open', status !== 'closed');
                } else {
                    console.warn(`Store hours not found for: ${name}`);
                    stateEl.textContent = 'Hours not available'; // Or some other default message
                    stateEl.classList.remove('close', 'open');
                }
            });
        }
    }

    customElements.define('store-locator-tabs', StoreLocatorTabs);
}
